/**
 * Workspace context — selected workspace_id flows down to every query
 * that needs it, and a workspace switcher (in Layout) sets it.
 *
 * Persistence: synced to URL ?workspace_id= AND localStorage so the
 * choice survives page navigation, hard reload, and new tab.
 */
import { Activity, createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { appLoginReturnPath } from "../../auth/login-return-path.js";
import { selectWorkspace, workspaceSearch } from "../lib/workspace-selection.js";

interface Workspace {
  id: string;
  name: string;
  workspace_type: string;
  owner_type: string;
  owner_id: string;
  can_write?: boolean;
}

interface WorkspaceCtx {
  current: Workspace | null;
  workspaces: Workspace[];
  setCurrent: (id: string) => void;
  isLoading: boolean;
  error: Error | null;
  retry: () => void;
}

const Ctx = createContext<WorkspaceCtx | null>(null);
const STORAGE_KEY = "cmcp:current_workspace_id";

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const route = useLocation();
  const navigate = useNavigate();
  const [savedId, setSavedId] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY); }
    catch { return null; }
  });
  // Resolve the URL during render, not after an effect: following an MCP
  // workspace link must never paint the previous workspace's records first.
  const requestedId = new URLSearchParams(route.search).get("workspace_id") || savedId;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => {
      const res = await fetch("/api/v1/workspaces", { credentials: "include" });
      if (res.status === 401) {
        const path = location.pathname;
        const publicSurface = path.startsWith("/preview")
          || path.startsWith("/dev/")
          || path.startsWith("/app/preview/")
          || path.startsWith("/app/dev/");
        if (!publicSurface) {
          const next = encodeURIComponent(appLoginReturnPath(path, location.search));
          window.location.replace(`/start?mode=signin&next=${next}`);
        }
        throw new Error("Not authenticated");
      }
      if (!res.ok) throw new Error("Failed to load workspaces");
      const json = await res.json();
      return (json.workspaces ?? []) as Workspace[];
    },
  });

  const selectedWorkspace = selectWorkspace(data ?? [], requestedId);
  // A failed membership refresh suspends access, but is not a workspace
  // switch. Keep the component identity so a transient outage cannot reset
  // page-local drafts. Only an actual selection change resets that state.
  const selectedId = selectedWorkspace?.id ?? null;
  const current = error ? null : selectedWorkspace;
  const currentId = current?.id ?? null;

  useEffect(() => {
    if (!currentId) return;
    try { localStorage.setItem(STORAGE_KEY, currentId); } catch { /* Storage may be disabled. */ }
    setSavedId(currentId);
    if (new URLSearchParams(route.search).get("workspace_id") !== currentId) {
      // Let React Router maintain its history key/index and notify every
      // useSearchParams consumer. Raw replaceState desynchronizes the router.
      navigate({ pathname: route.pathname, search: workspaceSearch(route.search, currentId), hash: route.hash }, {
        replace: true,
        state: route.state,
      });
    }
  }, [currentId, navigate, route.pathname, route.search, route.hash, route.state]);

  const setCurrent = (id: string) => {
    if (error || !data?.some((workspace) => workspace.id === id)) return;
    setSavedId(id);
    navigate({ pathname: route.pathname, search: workspaceSearch(route.search, id), hash: route.hash }, {
      state: route.state,
    });
  };
  const value: WorkspaceCtx = {
    current,
    workspaces: error ? [] : data ?? [],
    setCurrent,
    isLoading,
    error,
    retry: () => { void refetch(); },
  };

  // Activity hides the entire workspace and disconnects its effects while
  // preserving nested editor state. A null identity inside the hidden tree
  // would still remove children behind `if (!current)` guards, so retain only
  // a read-only identity there. No writable membership or workspace switching
  // is exposed until the API successfully verifies access again.
  const retainedValue: WorkspaceCtx = error && selectedWorkspace
    ? { ...value, current: { ...selectedWorkspace, can_write: false } }
    : value;

  return <Ctx.Provider value={value}>
    <Activity key={selectedId ?? "no-workspace"} mode={error ? "hidden" : "visible"}>
      <Ctx.Provider value={retainedValue}>{children}</Ctx.Provider>
    </Activity>
    {error && <div role="alert" className="mx-auto max-w-xl space-y-3 p-8 text-stone-700">
      <h1 className="text-lg font-semibold">Workspace access could not be verified</h1>
      <p>Your unsaved work is held here while access is unavailable. Retry to continue.</p>
      <button type="button" className="rounded-md bg-action px-4 py-2 font-semibold text-white" onClick={value.retry}>Retry workspace access</button>
    </div>}
  </Ctx.Provider>;
}

export function useWorkspace() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useWorkspace must be inside WorkspaceProvider");
  return v;
}
